<script type="module" lang="ts">
import { twMerge } from "tailwind-merge"

export type BadgeVariant =
  | "default"
  | "success"
  | "warning"
  | "error"
  | "primary"
  | "secondary"

export type BadgeSize = "default" | "small"
</script>
<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    class?: string
    variant?: BadgeVariant
    size?: "default" | "small"
    loading?: boolean
  }>(),
  { class: "", variant: "default", size: "default", loading: false },
)
const badgeSizeClass = computed(() => {
  if (props.size == "small") {
    return "gap-x-1 px-1.5 py-px pb-0.5 [&>svg]:size-2.5"
  }

  return "gap-x-[5px] px-[5px] py-px [&>svg]:size-3"
})
const textSize = computed(() => {
  if (props.size == "small") {
    return "text-xxs leading-3"
  }

  return "text-xs leading-4"
})
const badgeClass = computed(() => {
  if (props.variant === "success") {
    return "text-[#075A39] border-[#C7F4D3] bg-[#DDF9E4]"
  }

  if (props.variant === "warning") {
    return "text-[#705500] border-[#FFEBAD] bg-[#FFF3CC]"
  }

  if (props.variant === "error") {
    return "text-[#A52F2E] border-[#ffd4d3] bg-[#ffebeb]"
  }

  if (props.variant === "primary") {
    return "text-[#4711BB] border-[#E8DDFE] bg-[#F5EEFF]"
  }
  if (props.variant === "secondary") {
    return "text-gray-500 border-[#E6E7EA] bg-[#FBFBFB]"
  }

  return "text-[#183C81] border-[#D6E5FF] bg-[#E5EEFF]"
})
</script>
<template>
  <div
    :class="
      twMerge(
        'inline-flex items-center rounded-md border pr-1.5 align-middle tracking-normal text-nowrap',
        badgeClass,
        badgeSizeClass,
        props.class,
      )
    "
  >
    <svg
      v-if="loading"
      class="animate-spin"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
    >
      <path
        d="M9.805 8.403a4.5 4.5 0 1 1-7.61-4.806 4.5 4.5 0 0 1 7.61 4.806h0Z"
        stroke="#E8DDFE"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6 1.5a4.5 4.5 0 1 1-3.182 7.682"
        stroke="#9162F9"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    <slot v-if="$slots.icon && !loading" name="icon" />
    <span :class="textSize" class="font-medium tracking-normal">
      <slot />
    </span>
  </div>
</template>
